/**
 * useClickShield — the part that actually stops the user wandering off.
 *
 * The dim overlay is `pointer-events: none`, because an overlay that swallows
 * pointer events also swallows the drag from the node palette onto the canvas,
 * which is the single most important thing this tutorial teaches. So the overlay
 * is only paint, and refusal happens here instead: one set of capture-phase
 * listeners on `document` that cancels anything not whitelisted by the step.
 *
 * Capture at the document is deliberate. It runs before React's root listener
 * and before the window-level keyboard shortcuts in the canvas, so a single
 * `stopPropagation` covers synthetic handlers, native handlers, and ReactFlow's
 * own drag machinery alike.
 *
 * What is NOT blocked, and why:
 *   dragstart/dragover/drop  — the palette-to-canvas drag has to work
 *   mousemove/mouseup        — a connection drag is mousedown here, mouseup there
 *   wheel                    — the settings page is taller than the viewport
 */

import { useEffect } from 'react';

export const SHIELDED_EVENTS = ['pointerdown', 'mousedown', 'click', 'touchstart', 'keydown'];

/** Anything inside the tutorial's own UI is always live. */
export const TUTORIAL_UI_SELECTOR = '[data-tutorial-ui]';

const matchesAny = (el, selectors) => {
    for (const selector of selectors) {
        try {
            if (el.closest(selector)) return true;
        } catch (error) {
            // A bad selector should not lock the user out of the whole page.
            console.error('Tutorial selector is invalid:', selector, error);
        }
    }
    return false;
};

/**
 * @param {{getPolicy: () => {
 *   active: boolean,
 *   allow: string[],
 *   deny: string[],
 *   extraEvents?: string[],
 *   onAllowedClick?: (target: Element) => void,
 * }}} options
 * @returns {() => void} uninstall
 */
export function installShield({ getPolicy }) {
    const handler = (event) => {
        const policy = getPolicy();
        if (!policy || !policy.active) return;

        const target = event.target instanceof Element ? event.target : null;

        // No target we can reason about (a keydown on document, say) — refuse it
        // rather than guess, except when nothing is being guarded at all.
        if (!target) {
            if (event.type === 'keydown') block(event);
            return;
        }

        if (target.closest(TUTORIAL_UI_SELECTOR)) return;

        // Typing is never part of a step: every field the tutorial shows is
        // pre-filled, and this also disarms Delete/Backspace and Cmd+Arrow.
        if (event.type === 'keydown') {
            block(event);
            return;
        }

        if (policy.deny.length > 0 && matchesAny(target, policy.deny)) {
            block(event);
            return;
        }

        if (policy.allow.length === 0 || !matchesAny(target, policy.allow)) {
            block(event);
            return;
        }

        if (event.type === 'click' && policy.onAllowedClick) {
            policy.onAllowedClick(target);
        }
    };

    const block = (event) => {
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
        if (event.cancelable) event.preventDefault();
    };

    // The per-step extras (wheel, say) are registered up front and made
    // conditional inside the handler, so the listener set never churns.
    const types = [...SHIELDED_EVENTS, 'wheel'];
    const wrapped = (event) => {
        if (event.type === 'wheel') {
            const policy = getPolicy();
            if (!policy?.active || !policy.extraEvents?.includes('wheel')) return;
        }
        handler(event);
    };

    types.forEach((type) => document.addEventListener(type, wrapped, { capture: true, passive: false }));

    return () => {
        types.forEach((type) => document.removeEventListener(type, wrapped, { capture: true }));
    };
}

/** Installs the shield for the lifetime of the component. */
export default function useClickShield(getPolicy) {
    useEffect(() => installShield({ getPolicy }), [getPolicy]);
}
